import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Search, Upload, Bell, ShoppingBag, DollarSign, LogOut, Star, Menu, X, Shield, Brain, GraduationCap, Package } from 'lucide-react';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const FIELDS = ['All', 'Computer Science', 'Biology', 'Chemistry', 'Physics', 'Mathematics', 'Engineering', 'Medicine', 'Business', 'Psychology', 'Literature', 'History'];
const DOC_TYPES = ['All', 'notes', 'research'];

const Home = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedField, setSelectedField] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userResults, setUserResults] = useState([]);
  const [showUserResults, setShowUserResults] = useState(false);
  const [showCustomFieldFilter, setShowCustomFieldFilter] = useState(false);
  const [customFieldFilter, setCustomFieldFilter] = useState('');
  const [showCustomTypeFilter, setShowCustomTypeFilter] = useState(false);
  const [customTypeFilter, setCustomTypeFilter] = useState('');

  useEffect(() => {
    fetchDocuments();
    fetchUnreadNotifications();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications');
    }
  };

  const handleSearch = async () => {
    try {
      setShowUserResults(false);
      setUserResults([]);
      
      const params = {};
      if (selectedField !== 'All') params.field = selectedField;
      if (searchQuery) params.search = searchQuery;
      const response = await axios.get(`${API}/documents`, { params });
      let filtered = response.data;
      if (selectedType !== 'All') {
        filtered = filtered.filter(doc => doc.document_type === selectedType);
      }
      setDocuments(filtered);
      
      // Also search for users if there's a query
      if (searchQuery && searchQuery.length >= 2) {
        try {
          const userResponse = await axios.get(`${API}/users/search/${searchQuery}`);
          if (userResponse.data.length > 0) {
            setUserResults(userResponse.data);
            setShowUserResults(true);
          }
        } catch (error) {
          console.error('User search failed');
        }
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-blue-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl flex items-center justify-center shadow-md">
                <BookOpen className="w-6 h-6 text-blue-50" />
              </div>
              <span className="text-xl md:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>DocMarket</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-3 lg:gap-4">
              <Button
                data-testid="nav-upload-btn"
                onClick={() => navigate('/upload')}
                className="rounded-xl bg-blue-600 hover:bg-blue-700 h-10"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
              <Button
                data-testid="nav-study-builder-btn"
                onClick={() => navigate('/study-builder')}
                variant="outline"
                className="rounded-xl h-10 border-indigo-200 text-indigo-600 hover:bg-indigo-50"
              >
                <Brain className="w-4 h-4 mr-2" />
                Study Builder
              </Button>
              <Button
                data-testid="nav-courses-btn"
                onClick={() => navigate('/courses')}
                variant="outline"
                className="rounded-xl h-10 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <GraduationCap className="w-4 h-4 mr-2" />
                Courses
              </Button>
              <Button
                data-testid="nav-notifications-btn"
                onClick={() => navigate('/notifications')}
                variant="ghost"
                size="icon"
                className="rounded-xl relative h-10 w-10"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                )}
              </Button>
              <Button
                data-testid="nav-purchases-btn"
                onClick={() => navigate('/purchases')}
                variant="ghost"
                size="icon"
                className="rounded-xl h-10 w-10"
              >
                <ShoppingBag className="w-5 h-5" />
              </Button>
              <Button
                data-testid="nav-profile-btn"
                onClick={() => navigate(`/profile/${user.id}`)}
                variant="ghost"
                className="rounded-xl h-10"
              >
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-blue-100 text-blue-900">
                    {user.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
              </Button>
              {user.is_admin && (
                <Button
                  data-testid="nav-admin-btn"
                  onClick={() => navigate('/admin')}
                  variant="ghost"
                  size="icon"
                  className="rounded-xl h-10 w-10 text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Shield className="w-5 h-5" />
                </Button>
              )}
              <Button
                data-testid="nav-logout-btn"
                onClick={logout}
                variant="ghost"
                size="icon"
                className="rounded-xl h-10 w-10"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <Button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              variant="ghost"
              size="icon"
              className="md:hidden rounded-xl h-10 w-10"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-3 space-y-2">
              <Button
                data-testid="mobile-nav-upload-btn"
                onClick={() => { navigate('/upload'); setMobileMenuOpen(false); }}
                className="w-full justify-start rounded-xl bg-blue-600 hover:bg-blue-700 h-12"
              >
                <Upload className="w-5 h-5 mr-3" />
                Upload Document
              </Button>
              <Button
                data-testid="mobile-nav-study-builder-btn"
                onClick={() => { navigate('/study-builder'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12 border-indigo-200 text-indigo-600"
              >
                <Brain className="w-5 h-5 mr-3" />
                AI Study Builder
              </Button>
              <Button
                data-testid="mobile-nav-courses-btn"
                onClick={() => { navigate('/courses'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12 border-emerald-200 text-emerald-700"
              >
                <GraduationCap className="w-5 h-5 mr-3" />
                Browse Courses
              </Button>
              <Button
                data-testid="mobile-nav-notifications-btn"
                onClick={() => { navigate('/notifications'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12 relative"
              >
                <Bell className="w-5 h-5 mr-3" />
                Notifications
                {unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs px-2 py-1 rounded-full">{unreadCount}</span>
                )}
              </Button>
              <Button
                data-testid="mobile-nav-purchases-btn"
                onClick={() => { navigate('/purchases'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12"
              >
                <ShoppingBag className="w-5 h-5 mr-3" />
                My Purchases
              </Button>
              <Button
                data-testid="mobile-nav-earnings-btn"
                onClick={() => { navigate('/earnings'); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12"
              >
                <DollarSign className="w-5 h-5 mr-3" />
                My Earnings
              </Button>
              <Button
                data-testid="mobile-nav-profile-btn"
                onClick={() => { navigate(`/profile/${user.id}`); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12"
              >
                <Avatar className="w-6 h-6 mr-3">
                  <AvatarFallback className="bg-blue-100 text-blue-900 text-sm">
                    {user.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                My Profile
              </Button>
              {user.is_admin && (
                <Button
                  data-testid="mobile-nav-admin-btn"
                  onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                  variant="outline"
                  className="w-full justify-start rounded-xl h-12 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Shield className="w-5 h-5 mr-3" />
                  Admin Dashboard
                </Button>
              )}
              <Button
                data-testid="mobile-nav-logout-btn"
                onClick={() => { logout(); setMobileMenuOpen(false); }}
                variant="outline"
                className="w-full justify-start rounded-xl h-12 text-red-600 hover:text-red-700"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Logout
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-4 md:py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 rounded-2xl md:rounded-3xl p-6 md:p-12 mb-6 md:mb-8 shadow-xl text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>
          <div className="relative z-10">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
              Discover Academic Excellence
            </h1>
            <p className="text-base md:text-lg text-blue-50/90 max-w-2xl">
              Access high-quality study materials from students and scholars across all disciplines
            </p>
          </div>
        </div>

        {/* Search Section */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-6 md:mb-8 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-3 md:gap-4">
            <div className="flex-1">
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search documents, topics, tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="rounded-xl"
              />
            </div>
            {!showCustomFieldFilter ? (
              <Select value={selectedField} onValueChange={(v) => {
                if (v === 'custom') {
                  setShowCustomFieldFilter(true);
                  setSelectedField('');
                } else {
                  setSelectedField(v);
                }
              }}>
                <SelectTrigger data-testid="field-filter" className="w-full md:w-48 rounded-xl">
                  <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent>
                  {FIELDS.map(field => (
                    <SelectItem key={field} value={field}>{field}</SelectItem>
                  ))}
                  <SelectItem value="custom">+ Custom Field</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2 w-full md:w-48">
                <Input
                  data-testid="custom-field-filter-input"
                  type="text"
                  value={customFieldFilter}
                  onChange={(e) => {
                    setCustomFieldFilter(e.target.value);
                    setSelectedField(e.target.value);
                  }}
                  placeholder="Custom field..."
                  className="rounded-xl"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setShowCustomFieldFilter(false);
                    setCustomFieldFilter('');
                    setSelectedField('All');
                  }}
                  className="rounded-xl flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            {!showCustomTypeFilter ? (
              <Select value={selectedType} onValueChange={(v) => {
                if (v === 'custom') {
                  setShowCustomTypeFilter(true);
                  setSelectedType('');
                } else {
                  setSelectedType(v);
                }
              }}>
                <SelectTrigger data-testid="type-filter" className="w-full md:w-48 rounded-xl">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map(type => (
                    <SelectItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">+ Custom Type</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="flex gap-2 w-full md:w-48">
                <Input
                  data-testid="custom-type-filter-input"
                  type="text"
                  value={customTypeFilter}
                  onChange={(e) => {
                    setCustomTypeFilter(e.target.value);
                    setSelectedType(e.target.value);
                  }}
                  placeholder="Custom type..."
                  className="rounded-xl"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    setShowCustomTypeFilter(false);
                    setCustomTypeFilter('');
                    setSelectedType('All');
                  }}
                  className="rounded-xl flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <Button
              data-testid="search-btn"
              onClick={handleSearch}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
            >
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </div>
        </div>

        {/* User Search Results */}
        {showUserResults && userResults.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-4 md:p-6 mb-6 md:mb-8 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                Users Found ({userResults.length})
              </h2>
              <Button
                onClick={() => setShowUserResults(false)}
                variant="ghost"
                size="sm"
                className="rounded-lg"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userResults.map((foundUser) => (
                <Link
                  key={foundUser.id}
                  to={`/profile/${foundUser.id}`}
                  data-testid={`user-result-${foundUser.id}`}
                  className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-blue-50 hover:shadow-md transition-all border border-gray-200"
                >
                  <Avatar className="w-12 h-12">
                    <AvatarFallback className="bg-blue-100 text-blue-900">
                      {foundUser.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-800 truncate">{foundUser.name}</p>
                    <p className="text-sm text-gray-500 truncate">{foundUser.field_of_concentration || 'Student'}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {documents.map((doc) => (
            <Link
              key={doc.id}
              to={`/document/${doc.id}`}
              data-testid={`document-card-${doc.id}`}
              className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-200 overflow-hidden group transition-all duration-300 hover:-translate-y-1"
            >
              <div className="p-4 md:p-6">
                <div className="flex items-start justify-between mb-3">
                  <span className="px-3 py-1 bg-blue-100 text-blue-900 text-xs font-medium rounded-full">
                    {doc.document_type}
                  </span>
                  <span className="text-2xl font-bold text-blue-800" style={{ fontFamily: 'Crimson Text, serif' }}>
                    ${doc.price.toFixed(2)}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {doc.title}
                </h3>
                <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                  {doc.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className="bg-blue-100 text-blue-900 text-xs">
                        {doc.seller_name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm text-gray-600">{doc.seller_name}</span>
                  </div>
                  {doc.average_rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="text-sm font-medium text-gray-700">{doc.average_rating.toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {doc.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-lg">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {documents.length === 0 && (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No documents found</p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-4 md:py-6 mt-8 md:mt-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4">
            <p className="text-xs md:text-sm text-gray-600 text-center md:text-left">
              © {new Date().getFullYear()} DocMarket. All rights reserved.
            </p>
            <div className="flex gap-4 md:gap-6">
              <Link to="/terms" className="text-xs md:text-sm text-gray-600 hover:text-blue-600 transition-colors">
                Terms of Use
              </Link>
              <Link to="/privacy" className="text-xs md:text-sm text-gray-600 hover:text-blue-600 transition-colors">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
